// gd-carousel.js — horizontal scroll-snap with optional arrow controls + dot indicators.
// Matches Bolt patterns in CommunityGallery, FeaturedShades, ShadeShowcase, HowToApply,
// BeyondBeauty, SalonKitNailArtReels, CommunityLovers, RelatedColours.
//
// Usage: any element with [data-gd-carousel] attribute is auto-wired.
// Expected DOM (see snippets/gd-carousel-wrapper.liquid):
//   <div data-gd-carousel>
//     <div class="gd-carousel__head">
//       ... heading ...
//       <button data-gd-carousel-prev>...</button>
//       <button data-gd-carousel-next>...</button>
//     </div>
//     <div class="gd-carousel__track" data-gd-carousel-track>
//       ... cards (direct children) ...
//     </div>
//     <div class="gd-carousel__dots" data-gd-carousel-dots></div>   (optional)
//   </div>

(function () {
  'use strict';

  const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReduced = mq && mq.matches;

  function cardWidth(track) {
    const first = track.firstElementChild;
    if (!first) return 300;
    return first.offsetWidth || 300;
  }

  function gap(track) {
    const styleGap = getComputedStyle(track).gap;
    const parsed = parseFloat(styleGap);
    return isNaN(parsed) ? 16 : parsed;
  }

  function startOffset(track) {
    return track.firstElementChild ? track.firstElementChild.offsetLeft : 0;
  }

  function updateArrowState(ctx) {
    const { track, prev, next } = ctx;
    const start = startOffset(track);
    if (prev) prev.disabled = track.scrollLeft <= start + 4;
    if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
  }

  function activeIndex(track) {
    const step = cardWidth(track) + gap(track);
    if (step <= 0) return 0;
    return Math.round((track.scrollLeft - startOffset(track)) / step);
  }

  // Joel 2026-04-29: dot count + active dot now use VIEWPORT-PAGE
  // math, not 1-card-per-dot. With scrollWidth=2400 / clientWidth=1200
  // there are 2 pages of content → 2 dots, not 8 (one per card).
  // Active dot = round(scrollLeft/clientWidth), so the LAST dot is
  // active when the user reaches scroll-end (no stragglers right of it)
  // and the FIRST when at start. Auto-hides when content fits without
  // overflow. Recomputes on resize via the existing ResizeObserver.
  function pageCount(track) {
    if (track.scrollWidth - track.clientWidth <= 4) return 0;
    return Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth)));
  }

  function activePage(track) {
    return Math.max(0, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
  }

  function buildDots(ctx) {
    const { track, dots } = ctx;
    if (!dots) return;
    const count = pageCount(track);
    if (count === 0) {
      dots.innerHTML = '';
      dots.hidden = true;
      return;
    }
    dots.hidden = false;
    if (dots.children.length === count) {
      // Already correct count — just refresh active state.
      updateDots(ctx);
      return;
    }
    dots.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gd-carousel__dot';
      dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
      const idx = i;
      dot.addEventListener('click', () => {
        const max = track.scrollWidth - track.clientWidth;
        const target = Math.min(max, idx * track.clientWidth);
        track.scrollTo({ left: target, behavior: prefersReduced ? 'auto' : 'smooth' });
      });
      dots.appendChild(dot);
    }
    updateDots(ctx);
  }

  function updateDots(ctx) {
    const { dots, track } = ctx;
    if (!dots || dots.children.length === 0) return;
    const max = dots.children.length - 1;
    const active = Math.min(max, activePage(track));
    Array.prototype.forEach.call(dots.children, (dot, i) => {
      dot.classList.toggle('is-active', i === active);
    });
  }

  function step(ctx, direction) {
    const { track } = ctx;
    const amount = cardWidth(track) + gap(track);
    track.scrollBy({
      left: direction === 'prev' ? -amount : amount,
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }

  function init(el) {
    if (el.dataset.gdCarouselInit === '1') return;
    el.dataset.gdCarouselInit = '1';

    const track = el.querySelector('[data-gd-carousel-track]');
    if (!track) return;

    const prev = el.querySelector('[data-gd-carousel-prev]');
    const next = el.querySelector('[data-gd-carousel-next]');
    const dots = el.querySelector('[data-gd-carousel-dots]');

    const ctx = { el, track, prev, next, dots };

    if (prev) prev.addEventListener('click', () => step(ctx, 'prev'));
    if (next) next.addEventListener('click', () => step(ctx, 'next'));

    track.addEventListener(
      'scroll',
      () => {
        updateArrowState(ctx);
        updateDots(ctx);
      },
      { passive: true }
    );

    const ro = new ResizeObserver(() => {
      updateArrowState(ctx);
      buildDots(ctx);  // page count depends on clientWidth — recompute
    });
    ro.observe(track);

    buildDots(ctx);
    updateArrowState(ctx);
    updateDots(ctx);

    // Image-load can change scrollWidth — recompute after a tail tick.
    setTimeout(() => buildDots(ctx), 400);
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-gd-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  window.gdCarousel = { init, initAll };
})();
